import React, { memo, useMemo } from 'react';
import { View, StyleSheet } from 'react-native';
import { Text } from '@/components/common/text';
import { GlassCard } from '@/components/shared/redesign';
import { useAdminColors } from '@/hooks/admin/useAdminColors';
import type { CorkBoardTag } from '@/types/residents/corkBoard';

interface TagStatsHeaderProps {
  tags: CorkBoardTag[];
}

interface StatItem {
  label: string;
  value: number;
}

export const TagStatsHeader: React.FC<TagStatsHeaderProps> = memo(({ tags }) => {
  const { redesign } = useAdminColors();

  const stats = useMemo<StatItem[]>(() => {
    const total = tags.length;
    const active = tags.filter((t) => t.isEnabled).length;
    return [
      { label: 'Total', value: total },
      { label: 'Active', value: active },
      { label: 'Hidden', value: total - active },
    ];
  }, [tags]);

  return (
    <View style={styles.row}>
      {stats.map((s) => (
        <GlassCard key={s.label} padding={0} style={styles.card}>
          <View style={styles.cardInner}>
            <Text style={[styles.label, { color: redesign.text.subtle }]}>
              {s.label.toUpperCase()}
            </Text>
            <Text style={[styles.value, { color: redesign.text.primary }]}>
              {s.value}
            </Text>
          </View>
        </GlassCard>
      ))}
    </View>
  );
});

TagStatsHeader.displayName = 'TagStatsHeader';

const styles = StyleSheet.create({
  row: {
    flexDirection: 'row',
    gap: 12,
    marginBottom: 16,
  },
  card: {
    flex: 1,
  },
  cardInner: {
    paddingHorizontal: 14,
    paddingVertical: 12,
  },
  label: {
    fontSize: 10,
    fontWeight: '600',
    letterSpacing: 0.6,
  },
  value: {
    fontSize: 22,
    fontWeight: '600',
    marginTop: 4,
  },
});
